import {Avatar, Chip, FormControl, InputLabel, MenuItem, Select} from '@material-ui/core';
import React, {ReactNode, useState} from 'react';
import {AgentNumberStatusId} from 'Shared/constants/AgentNumberStatus';
import {AgentNumber} from 'Users/models/User/Agent/AgentNumber';
import './AgentNumberInput.sass';

export type AvailableAgentNumber = {
  value: string;
  isNewYork: boolean;
  status: {id: AgentNumberStatusId; name: string};
};

type AgentNumberInputProps = {
  availableAgentNumbers: AvailableAgentNumber[];
  agentNumbers: AgentNumber[];
  className?: string;
  onAddAgentNumber: (agentNumber: AvailableAgentNumber) => void;
  onRemoveAgentNumber: (agentNumber: AvailableAgentNumber) => void;
  disabled?: boolean;
};

const agentNumberToOption = ({isNewYork, value}: {isNewYork: boolean; value: string}) => `${value}|${isNewYork}`;
const optionToAgentNumber = (agentNumberOption: string) => {
  const [value, isNewYorkString] = agentNumberOption.split('|');

  return {
    value,
    isNewYork: isNewYorkString === 'true',
  };
};

const getAgentNumberSuffix = (agentNumber: AvailableAgentNumber) =>
  `${agentNumber.value}${agentNumber.isNewYork ? ' - New York' : ''}`;

const getAgentNumberStatus = (statusId: AgentNumberStatusId) =>
  statusId === AgentNumberStatusId.Inactive
    ? ' - (Inactive)'
    : statusId === AgentNumberStatusId.Terminated
    ? ' - (Terminated)'
    : '';

const AgentNumberInput = (props: AgentNumberInputProps) => {
  const {availableAgentNumbers, agentNumbers, className, onAddAgentNumber, onRemoveAgentNumber, disabled} = props;
  const [inputLabel, setInputLabel] = useState<HTMLLabelElement | null>();

  return (
    <FormControl className={className} variant="outlined" fullWidth>
      <InputLabel ref={el => setInputLabel(el)} id={`agent-number-select`}>
        Agent Numbers
      </InputLabel>
      <Select
        labelWidth={inputLabel?.clientWidth}
        multiple
        fullWidth
        variant="outlined"
        labelId={`agent-number-select`}
        renderValue={(agentNumberValues: any) => {
          const chips = agentNumberValues.map((agentNumberValue: string) => {
            const agentNumberOption = optionToAgentNumber(agentNumberValue);
            const agentNumber = availableAgentNumbers.find(
              an => an.value === agentNumberOption.value && an.isNewYork === agentNumberOption.isNewYork
            )!;

            return (
              agentNumber && (
                <Chip
                  className="agent-number-select__agent-number-chip"
                  color="primary"
                  key={agentNumberValue}
                  avatar={(agentNumber.isNewYork ? <Avatar>NY</Avatar> : null) as any}
                  label={
                    <>
                      <span>{agentNumber.value}</span>
                      <em>{getAgentNumberStatus(agentNumber.status.id)}</em>
                    </>
                  }
                />
              )
            );
          }) as ReactNode;

          return <div className="agent-number-select__chips-container">{chips}</div>;
        }}
        value={agentNumbers.map(agentNumberToOption)}
        onChange={(e, menuEl) => {
          const agentNumberOption = optionToAgentNumber((menuEl as any).props.value);
          const agentNumber = availableAgentNumbers.find(
            an => an.value === agentNumberOption.value && an.isNewYork === agentNumberOption.isNewYork
          )!;
          const isAlreadySelected = agentNumbers.some(
            an => an.value === agentNumberOption.value && an.isNewYork === agentNumberOption.isNewYork
          );

          if (isAlreadySelected) {
            onRemoveAgentNumber(agentNumber);
          } else {
            onAddAgentNumber(agentNumber);
          }
        }}
        disabled={!!disabled}
      >
        {availableAgentNumbers.map(agentNumber => (
          <MenuItem key={`${agentNumber.value}`} value={agentNumberToOption(agentNumber)}>
            {getAgentNumberSuffix(agentNumber)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default AgentNumberInput;
